<script context="module" lang="ts">
    import { writable } from "svelte/store"
    import type { NavigableClass } from "@circleofa/acim-engine"
    import { searchReady, accessibleSearch, initSearchWorkers } from "@utils/search/initSearch"

    let searchFocussed = writable(false)
    let searchValue = writable("")
    export const navigatorOpen = writable(false)
    type NavigatorTarget = {
        notation: string
        exact: boolean
        clearSearch: boolean
    }
    type NavigatorTargetOptions = {
        exact?: boolean
        clearSearch?: boolean
    }
    let focusNavigatorTarget: ((target: NavigatorTarget) => void) | null = null
    let pendingNavigatorTarget: NavigatorTarget | null = null

    export let open = () => navigatorOpen.set(true)
    export let toggle = () => navigatorOpen.update((n) => !n)
    export let focus = open // deprecated
    export function openToNotation(notation: string, options: NavigatorTargetOptions = {}) {
        const target = {
            notation,
            exact: options.exact ?? true,
            clearSearch: options.clearSearch ?? true,
        }
        pendingNavigatorTarget = target
        navigatorOpen.set(true)
        focusNavigatorTarget?.(target)
    }
    export let navigatorWidth = writable(0)

    let searchInput: HTMLInputElement

    registerHotKeyDoubleTap("Shift", async () => {
        // For some reason if it isn't loaded, let's just escape
        if (!searchInput) return

        open()
        await new Promise((res) => setTimeout(res, 40))
        searchInput.focus()
        searchInput.select()
    })
</script>

<script lang="ts">
    import { fade } from "svelte/transition"
    import SearchResults from "./SearchResults.svelte"
    import { acim } from "@utils/acim"
    import SubPageList from "./SubPageList.svelte"
    import { isNavigable } from "@circleofa/acim-engine"
    import { headerHeight } from "@components/TopBar/TopBar.svelte"
    import { footerHeight } from "@components/Footer.module"
    import { transitioning } from "@utils/directives/useAnimating"
    import { onDestroy, onMount, tick } from "svelte"
    import { deviceSize } from "@utils/material/responsive"
    import Icon from "@components/material/Icon.svelte"
    import { registerHotKeyDoubleTap } from "@utils/underhood/registerHotkey"
    import { clickEnter } from "@utils/directives/accessibility"
    import { page } from "$app/stores"
    import { scrollLockClass } from "@utils/highlights/directive-logic/scroll-locker"
    import EscapeHotkeyHelper from "@components/utilities/EscapeHotkeyHelper.svelte"
    import { delayedClick } from "@utils/helpers/delayed-click"

    function clickOutside(e: Event) {
        if ($navigatorOpen) {
            e.preventDefault()
            e.stopPropagation()
            $navigatorOpen = false
        }
    }

    const uniqueIDOpenList = writable<string[]>([])

    //
    //  FOCUS
    //

    let focussed: NavigableClass | null = null

    $: processNotation($page.params.notation)
    function processNotation(notation: string | undefined) {
        if (notation) focusNavigationNotation(notation, { exact: false })
    }

    onMount(() => {
        focusNavigatorTarget = focusRequestedTarget
        if (pendingNavigatorTarget) focusRequestedTarget(pendingNavigatorTarget)

        return () => {
            if (focusNavigatorTarget === focusRequestedTarget) focusNavigatorTarget = null
        }
    })

    function focusRequestedTarget(target: NavigatorTarget) {
        pendingNavigatorTarget = null
        if (target.clearSearch) {
            searchValue.set("")
            searchFocussed.set(false)
        }
        focusNavigationNotation(target.notation, { exact: target.exact })
    }

    function focusNavigationNotation(notation: string, { exact }: { exact: boolean }) {
        const foundNav = findNavigableFromNotation(notation, exact)
        if (!foundNav) {
            focussed = null
            return
        }

        focussed = foundNav
        openParentsFor(foundNav)
    }

    function findNavigableFromNotation(notation: string, exact: boolean) {
        const path = notation.split("#")[0]

        if (exact) {
            const exactFound = acim.find(path)
            if (exactFound && isNavigable(exactFound)) {
                const exactNav = acim.pageList.findPage(exactFound)
                if (exactNav) return exactNav
            }
        }

        const found = acim.findRenderable(path)
        if (!found) return null

        return acim.pageList.findPage(found)
    }

    function openParentsFor(foundNav: NavigableClass) {
        const newArray = []
        let currentNav: NavigableClass | undefined | null = foundNav

        while (currentNav) {
            if (!currentNav.parent) break
            newArray.push(currentNav.uniqueID)
            currentNav = currentNav.parent
        }

        uniqueIDOpenList.set(newArray)
    }

    // $: if ($navigatorOpen == false) focussed = null

    //
    //  SEARCH FUNCTIONS
    //

    let navigateToFirstSearch: () => void

    // Timeout is because if you click on search and then click "advanced", it disappears b/c unfocussed
    let searchVisible = false
    $: {
        $searchValue.length
        $searchFocussed
        processSearchVisibility()
    }
    function processSearchVisibility() {
        setTimeout(
            () => (searchVisible = $searchValue.length || $searchFocussed ? true : false),
            10,
        )
    }

    const duration = 400
    let widthSettleTimeout: ReturnType<typeof setTimeout> | undefined

    // Scrolling
    let focussedElement: HTMLElement
    let lastFocussedElement: HTMLElement
    let scrollToIt: () => void = () => null
    $: if (focussedElement) assignScroll()

    function assignScroll() {
        if (focussedElement == lastFocussedElement) return
        lastFocussedElement = focussedElement

        scrollToIt = async () => {
            scrollToIt = () => null
            await new Promise((r) => setTimeout(r, 200))
            focussedElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            })
        }
    }

    function closeNavigatorIfNotDesktop() {
        if ($deviceSize != "desktop") {
            $navigatorOpen = false
        }
    }

    const mainSubpages = acim.pageList.subpages
    if (!mainSubpages) throw new Error("No main subpages found - this should never happen")

    let navigatorElement: HTMLElement

    $: if (navigatorElement) {
        $deviceSize
        $navigatorOpen
        const node = navigatorElement
        tick().then(() => queueSettledNavigatorWidthUpdate(node))
    }

    onDestroy(() => clearTimeout(widthSettleTimeout))

    function queueSettledNavigatorWidthUpdate(node: HTMLElement) {
        clearTimeout(widthSettleTimeout)
        if ($deviceSize !== "desktop") {
            updateNavigatorWidth(node)
            return
        }

        widthSettleTimeout = setTimeout(() => settleNavigatorWidth(node), duration + 50)
    }

    function settleNavigatorWidth(node: HTMLElement) {
        clearTimeout(widthSettleTimeout)
        $navigatorWidth =
            $deviceSize === "desktop" && $navigatorOpen ? node.getBoundingClientRect().width : 0
    }

    function updateNavigatorWidth(node: HTMLElement) {
        if ($deviceSize !== "desktop") {
            $navigatorWidth = 0
            return
        }

        const rect = node.getBoundingClientRect()
        // During the slide animation we want the exposed width; once settled,
        // lock to the semantic open/closed state so late transition events cannot reset it.
        if (!node.classList.contains("transitioning")) {
            $navigatorWidth = $navigatorOpen ? rect.width : 0
            return
        }

        const offsetParent = node.offsetParent as HTMLElement | null
        const parentLeft = offsetParent?.getBoundingClientRect().left ?? 0
        const untransformedLeft = parentLeft + node.offsetLeft
        const exposedWidth = Math.max(0, Math.min(rect.width, rect.right - untransformedLeft))

        $navigatorWidth = exposedWidth
    }
</script>

{#if $navigatorOpen}
    <EscapeHotkeyHelper name="SideMenu" fn={() => navigatorOpen.set(false)} />
{/if}
<!-- {#if $navigatorOpen}  // deprecated -->
<aside
    role="navigation"
    aria-hidden={!$navigatorOpen}
    id="navigator"
    bind:this={navigatorElement}
    style={`top: calc(${$headerHeight}px + 1rem); bottom: calc(${$footerHeight}px + 1rem)`}
    on:transitionend={() => scrollToIt()}
    class:open={$navigatorOpen}
    use:transitioning={{
        onAnimationFrame: updateNavigatorWidth,
        onTransitionSettled: settleNavigatorWidth,
        properties: ["transform", "left"],
    }}
>
    <div
        role="button"
        aria-label="close"
        on:keydown={clickEnter}
        tabindex={0}
        class="close"
        on:click={() => ($navigatorOpen = false)}
    >
        <Icon icon="close" />
    </div>
    <div id="navigatorScroll" class="{scrollLockClass} relative">
        <div class="heading">
            <h2>A Course in Miracles</h2>
            <span>Complete & Annotated Edition</span>
        </div>

        <div class="search navigator-search">
            <div class="navigator-search-shell">
                <div class="inputwrapper">
                    <input
                        class="outline-none"
                        type="text"
                        on:keydown={({ key, shiftKey }) =>
                            key == "Enter" && shiftKey && navigateToFirstSearch()}
                        bind:this={searchInput}
                        on:focus={() => ($searchReady ? null : initSearchWorkers())}
                        on:focus={() => ($searchFocussed = true)}
                        on:blur={() => ($searchFocussed = false)}
                        bind:value={$searchValue}
                        placeholder="Quick search"
                    />
                    {#if $searchValue.length}
                        <div
                            tabindex="0"
                            role="button"
                            on:keydown={clickEnter}
                            transition:fade|global={{ duration: 100 }}
                            class="clear"
                            on:click={() => ($searchValue = "")}
                            title="clear search"
                        >
                            <Icon icon="close" />
                        </div>
                    {/if}
                </div>
            </div>
        </div>
        <div class="flexEnd">
            {#if searchVisible}
                <div transition:fade|global={{ duration: duration * 0.5 }}>
                    {#if $searchValue.length}
                        <SearchResults
                            on:navigate={closeNavigatorIfNotDesktop}
                            search={$searchValue}
                            bind:navigateToFirstSearch
                        />
                    {:else}
                        <p class="centerDescription">
                            Enter one or more keywords above to do a quick search, or try our <a
                                class="link underline"
                                on:click={closeNavigatorIfNotDesktop}
                                href={"/search"}>Advanced Search Tool</a
                            >
                        </p>
                    {/if}
                </div>
            {:else}
                <div transition:fade|global={{ duration: duration * 0.5 }}>
                    <div class="pageList">
                        <h3>Table of Contents</h3>
                        <div class="listgrid first">
                            {#each mainSubpages.slice(0, 1) as navigable}
                                <SubPageList
                                    bind:focussedElement
                                    {uniqueIDOpenList}
                                    on:navigate={closeNavigatorIfNotDesktop}
                                    {navigable}
                                    {focussed}
                                />
                            {/each}
                        </div>
                        <h4>A Course in Miracles CE</h4>
                        <div class="listgrid first">
                            {#each mainSubpages.slice(1, 6) as navigable}
                                <SubPageList
                                    bind:focussedElement
                                    {uniqueIDOpenList}
                                    on:navigate={closeNavigatorIfNotDesktop}
                                    {navigable}
                                    {focussed}
                                />
                            {/each}
                        </div>
                        <h4>Additional Content</h4>
                        <div class="listgrid first">
                            {#each mainSubpages.slice(6) as navigable}
                                <SubPageList
                                    bind:focussedElement
                                    {uniqueIDOpenList}
                                    on:navigate={closeNavigatorIfNotDesktop}
                                    {navigable}
                                    {focussed}
                                />
                            {/each}
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</aside>
{#if $navigatorOpen}
    <div
        role="button"
        tabindex="-1"
        use:delayedClick={{ callback: clickOutside }}
        class="navigatorBack navigator-backdrop--contents"
        transition:fade={{ duration }}
    ></div>
{/if}
